import { useEffect, useRef, useCallback, useState } from 'react';

interface TurnstileProps {
  siteKey: string;
  onSuccess: (token: string) => void;
  onError?: () => void;
  onExpire?: () => void;
}

declare global {
  interface Window {
    turnstile: {
      render: (element: HTMLElement, options: any) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

/**
 * Turnstile component that safely handles Cloudflare's DOM manipulation
 * without conflicting with React's virtual DOM.
 * 
 * The key insight is that we render Turnstile into a portal-like container
 * that React doesn't try to reconcile, preventing removeChild errors.
 */
export function Turnstile({ siteKey, onSuccess, onError, onExpire }: TurnstileProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const turnstileContainerRef = useRef<HTMLDivElement | null>(null);
  const [hasError, setHasError] = useState(false);
  const mountedRef = useRef(true);
  
  // Store callbacks in refs to avoid re-renders triggering widget recreation
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const onExpireRef = useRef(onExpire);
  
  useEffect(() => {
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
    onExpireRef.current = onExpire;
  }, [onSuccess, onError, onExpire]);

  const cleanupWidget = useCallback(() => {
    // Remove the Turnstile widget if it exists
    if (widgetIdRef.current && window.turnstile) {
      try {
        window.turnstile.remove(widgetIdRef.current);
      } catch {
        // Widget may already be removed, ignore
      }
      widgetIdRef.current = null;
    }
    
    // Remove our manually created container from the DOM
    // This is safe because we created it ourselves, not React
    if (turnstileContainerRef.current && turnstileContainerRef.current.parentNode) {
      try {
        turnstileContainerRef.current.parentNode.removeChild(turnstileContainerRef.current);
      } catch {
        // Already removed, ignore
      }
      turnstileContainerRef.current = null;
    }
  }, []);

  const renderWidget = useCallback(() => {
    if (!containerRef.current || !window.turnstile || !mountedRef.current) return;
    
    // Clean up any existing widget first
    cleanupWidget();
    
    // Create a dedicated container for Turnstile
    // This container is managed by us, not React, so Turnstile can modify it freely
    const turnstileContainer = document.createElement('div');
    turnstileContainer.className = 'turnstile-widget-container';
    turnstileContainer.setAttribute('data-turnstile-managed', 'true');
    turnstileContainerRef.current = turnstileContainer;
    
    // Append to our ref container
    containerRef.current.appendChild(turnstileContainer);

    try {
      const widgetId = window.turnstile.render(turnstileContainer, {
        sitekey: siteKey,
        callback: (token: string) => {
          if (mountedRef.current) {
            onSuccessRef.current(token);
          }
        },
        'error-callback': () => {
          if (mountedRef.current && onErrorRef.current) {
            onErrorRef.current();
          }
        },
        'expired-callback': () => {
          if (mountedRef.current && onExpireRef.current) {
            onExpireRef.current();
          }
        },
        'refresh-expired': 'auto',
        retry: 'auto',
      });

      widgetIdRef.current = widgetId;
    } catch (error) {
      console.error('Error rendering Turnstile widget:', error);
      if (mountedRef.current) {
        setHasError(true);
        if (onErrorRef.current) {
          onErrorRef.current();
        }
      }
    }
  }, [siteKey, cleanupWidget]);

  useEffect(() => {
    mountedRef.current = true;
    
    if (!containerRef.current) return;

    // If Turnstile is already loaded, render immediately
    if (window.turnstile) {
      renderWidget();
    } else {
      // Poll for Turnstile to load
      const interval = setInterval(() => {
        if (window.turnstile && mountedRef.current) {
          clearInterval(interval);
          renderWidget();
        }
      }, 100);
      
      // Timeout after 10 seconds
      const timeout = setTimeout(() => {
        clearInterval(interval);
        if (!window.turnstile && mountedRef.current) {
          console.error('Turnstile script failed to load within 10 seconds');
          setHasError(true);
          if (onErrorRef.current) onErrorRef.current();
        }
      }, 10000);

      return () => {
        clearInterval(interval);
        clearTimeout(timeout);
        mountedRef.current = false;
        cleanupWidget();
      };
    }

    return () => {
      mountedRef.current = false;
      cleanupWidget();
    };
  }, [renderWidget, cleanupWidget]);

  if (hasError) {
    return (
      <div className="flex justify-center my-4 p-4 border border-red-300 rounded bg-red-50">
        <p className="text-red-600 text-sm">Verification widget failed to load. Please refresh the page.</p>
      </div>
    );
  }
  
  return (
    <div className="flex justify-center my-4">
      {/* 
        This div is intentionally kept empty by React.
        We manually append Turnstile's container as a child.
        The 'suppressHydrationWarning' and empty children prevent React 
        from trying to reconcile the DOM that Turnstile creates.
      */}
      <div 
        ref={containerRef}
        suppressHydrationWarning
        style={{ minHeight: '65px', minWidth: '300px' }}
      />
    </div>
  );
}